/**
 * @file: SwipeableThreadRow.tsx
 *
 * Wraps a {@link ChatThreadRow} in a swipe-to-reveal action strip (置顶/静音/
 * 删除), matching the app's Swipeable idiom. The actions are passed in — the
 * row stays presentational, the list owns the toggle/delete (+ confirm). The
 * strip auto-closes on action tap. Pin/mute labels reflect the current state
 * so one button toggles both ways.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatThreadItem } from '@euler/model/chat';
import { FC, ReactNode, memo, useCallback, useRef } from 'react';
import { Animated, Text, TouchableOpacity, View } from 'react-native';
import Swipeable from 'react-native-gesture-handler/Swipeable';
import { ChatThreadRow } from './ChatThreadRow';

const ACTION_WIDTH = 72;
// Amber, not the app-accent blue — the blue would merge into a selected
// (solid-blue) row's background when its swipe actions are revealed.
const PIN_COLOR = '#f59e0b';
const MUTE_COLOR = '#9ca3af';
const DELETE_COLOR = '#ef4444';

export const SwipeableThreadRow: FC<{
  thread: ChatThreadItem;
  active?: boolean;
  /** Edge-to-edge bottom hairline (the pinned-section boundary). */
  fullWidthDivider?: boolean;
  onPress: () => void;
  onTogglePin: (thread: ChatThreadItem) => void;
  onToggleMute: (thread: ChatThreadItem) => void;
  onDelete: (thread: ChatThreadItem) => void;
}> = memo(
  ({
    thread,
    active,
    fullWidthDivider,
    onPress,
    onTogglePin,
    onToggleMute,
    onDelete,
  }) => {
    const { rpx } = useResponsiveContext();
    const ref = useRef<Swipeable>(null);

    const isPinned = !!thread.pinnedAt && Number(thread.pinnedAt) > 0;
    const isMuted = thread.mutedAt != null;

    const renderAction = useCallback(
      (
        progress: Animated.AnimatedInterpolation<number>,
        label: string,
        color: string,
        offset: number,
        onTap: () => void,
      ): ReactNode => {
        const translateX = progress.interpolate({
          inputRange: [0, 1],
          outputRange: [offset, 0],
        });
        return (
          <Animated.View style={{ flex: 1, transform: [{ translateX }] }}>
            <TouchableOpacity
              activeOpacity={0.7}
              style={{
                flex: 1,
                width: rpx(ACTION_WIDTH),
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: color,
              }}
              onPress={() => {
                ref.current?.close();
                onTap();
              }}
            >
              <Text
                style={{ color: '#fff', fontSize: rpx(12) }}
                numberOfLines={1}
              >
                {label}
              </Text>
            </TouchableOpacity>
          </Animated.View>
        );
      },
      [rpx],
    );

    const renderRightActions = useCallback(
      (progress: Animated.AnimatedInterpolation<number>) => (
        <View style={{ flexDirection: 'row', width: rpx(ACTION_WIDTH * 3) }}>
          {renderAction(
            progress,
            isPinned ? '取消置顶' : '置顶',
            PIN_COLOR,
            rpx(ACTION_WIDTH * 3),
            () => onTogglePin(thread),
          )}
          {renderAction(
            progress,
            isMuted ? '取消静音' : '静音',
            MUTE_COLOR,
            rpx(ACTION_WIDTH * 2),
            () => onToggleMute(thread),
          )}
          {renderAction(progress, '删除', DELETE_COLOR, rpx(ACTION_WIDTH), () =>
            onDelete(thread),
          )}
        </View>
      ),
      [
        rpx,
        renderAction,
        isPinned,
        isMuted,
        thread,
        onTogglePin,
        onToggleMute,
        onDelete,
      ],
    );

    return (
      <Swipeable
        ref={ref}
        renderRightActions={renderRightActions}
        overshootRight={false}
        containerStyle={{ backgroundColor: '#eeeef0' }}
      >
        <ChatThreadRow
          thread={thread}
          active={active}
          fullWidthDivider={fullWidthDivider}
          onPress={onPress}
        />
      </Swipeable>
    );
  },
);
